import "./AcceptInvitation.css";

import {
  OrganisationTypeConstants,
  ProjectAccessConstants,
  ProjectPermissionConstants,
} from "../../../../../../constants";
import { AssetManagerSingleCardTemplate, DeveloperSingleCardTemplate } from "../../../../../../templates";
import { useAuth } from "../../../../../../useAuth";
import { getFormattedDate } from "../../../../../../utils";
import { Button, Column, InformationBox, ReadonlyText, Row } from "../../../../../../widget";
import { useAcceptInvitation } from "./useAcceptInvitation";

export const AcceptInvitation = (): JSX.Element => {
  const { currentUserType } = useAuth();
  const {
    invitationDetails,
    handleBackClick,
    backButtonText,
    onAcceptInvitation,
    onRejectInvitation,
    hasPermission,
    isOnAcceptInvitationLoading,
    isOnRejectInvitationLoading,
  } = useAcceptInvitation();
  const isAssetManager = currentUserType === OrganisationTypeConstants.ASSET_MANAGER;
  const content = invitationDetails && (
    <>
      <Row spacingV="ll">
        <Column span={4}>
          <div>
            <h2>Accept project access</h2>
          </div>
        </Column>
      </Row>
      {invitationDetails?.calculatedStatus === ProjectAccessConstants.STATUS_SENT && (
        <Row spacingV="ll">
          <Column span={12}>
            <InformationBox variant="info">
              <div className="ProjectAccessAcceptInvitationInfo">
                {invitationDetails.type === "Request" ? (
                  <p className="body2">
                    <span>{invitationDetails.sentByUser?.fullName}</span> of{" "}
                    <span>{invitationDetails.sourceOrganisation?.displayName}</span> would like to request access to
                    your project, <span>{invitationDetails.project?.displayName}</span>.
                  </p>
                ) : (
                  <p className="body2">
                    <span>{invitationDetails.sentByUser?.fullName}</span> of{" "}
                    <span>{invitationDetails.sourceOrganisation?.displayName}</span> would like to grant your
                    organisation access to their project, <span>{invitationDetails.project?.displayName}</span>. If not
                    accepted or declined, this access invite will expire after 10 days.
                  </p>
                )}
              </div>
            </InformationBox>
          </Column>
        </Row>
      )}
      <Row spacingV="ll">
        <Column span={4}>
          <ReadonlyText label="Project name" value={invitationDetails.project?.displayName} />
        </Column>
        <Column span={4} offset={1}>
          <ReadonlyText label="Organisation" value={invitationDetails.sourceOrganisation?.displayName} />
        </Column>
      </Row>
      <Row spacingV="ll">
        <Column span={4}>
          <ReadonlyText label="Code" value={invitationDetails.project?.standard?.displayName} />
        </Column>
        <Column span={4} offset={1}>
          <ReadonlyText label="Access" value={invitationDetails.role} />
        </Column>
      </Row>
      <Row spacingV="ll">
        <Column span={4}>
          <ReadonlyText label="Project status" value={invitationDetails.project?.status} />
        </Column>
        <Column span={4} offset={1}>
          <ReadonlyText label="Created by" value={invitationDetails.sentByUser?.fullName} />
        </Column>
      </Row>
      <Row spacingV="ll">
        <Column span={4}>
          <ReadonlyText label="Sent at" value={getFormattedDate(invitationDetails.sentDate)} />
        </Column>
        {invitationDetails?.calculatedStatus !== ProjectAccessConstants.STATUS_ACCEPTED && (
          <Column span={4} offset={1}>
            <ReadonlyText label="Expires" value={getFormattedDate(invitationDetails.expiresDate)} />
          </Column>
        )}
      </Row>
      {invitationDetails?.calculatedStatus === ProjectAccessConstants.STATUS_SENT &&
        hasPermission(ProjectPermissionConstants.ACCEPT_REJECT_PROJECT_ORGANISATION_INVITATION) && (
          <div>
            <Button
              text="Accept"
              onClick={onAcceptInvitation}
              isLoading={isOnAcceptInvitationLoading}
              corners={isAssetManager ? "rounded" : "default"}
            />
            <Button
              text="Reject"
              variant="secondary"
              onClick={onRejectInvitation}
              isLoading={isOnRejectInvitationLoading}
              corners={isAssetManager ? "rounded" : "default"}
            />
          </div>
        )}
    </>
  );

  return (
    <div>
      {invitationDetails &&
        (isAssetManager ? (
          <AssetManagerSingleCardTemplate
            pageTitle={invitationDetails.project.displayName}
            handleBackClick={handleBackClick}
            backButtonText={backButtonText}
            activeElement="Projects"
          >
            {content}
          </AssetManagerSingleCardTemplate>
        ) : (
          <DeveloperSingleCardTemplate
            pageTitle={invitationDetails.project.displayName}
            handleBackClick={handleBackClick}
            backButtonText={backButtonText}
          >
            {content}
          </DeveloperSingleCardTemplate>
        ))}
    </div>
  );
};
